import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { checkRecipientDict, checkRecipientTypeDict, CheckRecipientTypes, payeeDetailsCheckLabel, USCountryFullName, yesNo } from "projects/money-movement-ui/src/app/shared/utilities/Constants";
import { combineLatest, Subject, takeUntil, filter, take } from "rxjs";
import { ChecksExtraPrompts, CheckTransactionDetails } from "../../../../../../create-request/models/form/TransactionForm";
import { selectCheckInfo, selectStagedTransactionForm } from "../../../../../../create-request/reducers";
import { CheckInfo } from "../../../../../../create-request/models/check/CheckInfo";
import { selectIsTransactionsViewPage } from "projects/money-movement-ui/src/app/transaction-view/reducers";
import { CodeAndDescription } from "../../../../../../create-request/models/shared/CodeAndDescription";
import { CheckActions } from "../../../../../../create-request/actions";

@Component({
  standalone: false,
    selector: 'mm-payee-details-check',
    templateUrl: './payee-details-check.component.html',
    styleUrls: ['./payee-details-check.component.scss']
})

export class PayeeDetailsCheckComponent implements OnInit, OnDestroy {
    readonly labels = payeeDetailsCheckLabel;
    readonly subtitle = 'Payee details';

    payeeNames: string[] = [];
    memo: string;
    deliveryAddress: string[] = [];
    isBsloaTransaction: boolean;
    useStandingAuth: string;
    isTransactionViewPage: boolean;
    countryList: CodeAndDescription[] = [];

    destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(private store: Store) {}

    ngOnInit(): void {
        this.store.select(selectIsTransactionsViewPage).pipe(takeUntil(this.destroy$)).subscribe(value => this.isTransactionViewPage = value);

        // Load country list silently without triggering full reducer updates
        this.store.select(selectCheckInfo).pipe(
            takeUntil(this.destroy$),
            filter(info => info.countryList.length === 0 && !info.inputCallFinished && !info.inputCallSucceeded),
            take(1)
        ).subscribe(() => {
            this.store.dispatch(CheckActions.callOneTimeInputSilent());
        });

        combineLatest([
            this.store.select(selectStagedTransactionForm),
            this.store.select(selectCheckInfo)
        ]).pipe(takeUntil(this.destroy$)).subscribe(([form, info]) => {
            let details = form.transactionDetails as CheckTransactionDetails;
            let extraPrompts = form.extraPrompts as ChecksExtraPrompts;
            this.isBsloaTransaction = extraPrompts.useStandingAuth === 'bundled';
            this.useStandingAuth = this.isBsloaTransaction ? extraPrompts?.useBundledStandingAuth === 'recurring' ? 'no' : extraPrompts?.useBundledStandingAuth! : extraPrompts?.useStandingAuth;
            this.payeeNames = this.useStandingAuth === yesNo.yes ? info.defaultPayee : this.getPayeeNames(details, info);
            this.memo = details.checkMemo ? details.checkMemo.trim() : '';
            this.countryList = info.countryList || [];
            this.deliveryAddress = this.getDeliveryAddress(details, info);
        })
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    getPayeeNames(transactionDetails: CheckTransactionDetails, info: CheckInfo): string[] {
        let payeeNames: string[] = [];
        if (transactionDetails.modifiedPayeeCheckbox) {
            payeeNames.push(transactionDetails?.modifiedPayeeName1 ?? '');
            payeeNames.push(transactionDetails?.modifiedPayeeName2 ?? '');
            payeeNames.push(transactionDetails?.modifiedPayeeName3 ?? '');
        } else if (transactionDetails.payeeType === 'firstParty') {
            payeeNames = (info.payee).filter((name: string) => !!name.trim());
        } else if (this.isTransactionViewPage) {
            payeeNames.push(info.payeeDetails.payeeName1);
            payeeNames.push(info.payeeDetails.payeeName2);
            payeeNames.push(info.payeeDetails.payeeName3);
        } else {
            if (transactionDetails.payeeName) payeeNames.push(transactionDetails.payeeName.toUpperCase());
            if (transactionDetails.additionalPayeeName1) payeeNames.push(transactionDetails.additionalPayeeName1.toUpperCase());
            if (transactionDetails.additionalPayeeName2) payeeNames.push(transactionDetails.additionalPayeeName2.toUpperCase());
        }
        return payeeNames;
    }

    getDeliveryAddress(transactionDetails: CheckTransactionDetails, info: CheckInfo): string[] {
        let deliveryAddress: string[] = [];
        let recipient = checkRecipientDict[transactionDetails.recipient!] as CheckRecipientTypes;
        if ((transactionDetails.recipient === checkRecipientTypeDict.addressofrecord || ((transactionDetails.recipient === checkRecipientTypeDict.addressdomestic || transactionDetails.recipient === checkRecipientTypeDict.addressforeign) && this.useStandingAuth === yesNo.yes)) && !!info.defaultRecipient) {
            deliveryAddress = [...info.defaultRecipient];
        } else if (transactionDetails.country === 'US' && info.countryCode === 'US') {
            deliveryAddress = [...info.deliveryAddress];
            deliveryAddress.push(USCountryFullName)
        } else {
            deliveryAddress = [...info.deliveryAddress];
            // For international addresses, replace country code with country name from the country list
            if (transactionDetails.country && this.countryList.length > 0) {
                const countryName = this.countryList.find(country => country.code === transactionDetails.country!.toUpperCase())?.description;
                if (countryName) {
                    const lastElement = deliveryAddress[deliveryAddress.length - 1];
                    if (lastElement && (lastElement.toUpperCase() === this.countryList.find(country => country.code === transactionDetails.country!.toUpperCase())?.description || lastElement.toUpperCase() === transactionDetails.country!.toUpperCase())) {
                        deliveryAddress[deliveryAddress.length - 1] = countryName;
                    } else {
                        deliveryAddress.push(countryName);
                    }
                }
            }
        }
        //SAC-385335,SAC-385713 We do not receive the delivery address header label on the View page for Advisory Firm ..We should remove the header label from being shown on the View page
        this.isTransactionViewPage ? "" : deliveryAddress.unshift(recipient);
        return deliveryAddress;
    }
}
